import { AsyncPipe, DatePipe, NgOptimizedImage } from '@angular/common';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import { DocumentOwnerService } from '../../services/document-owners.service';
import { catchError, EMPTY } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentOwner } from '../../models/document-owner.model';
import { MatBadge } from '@angular/material/badge';
import { UserService } from '../../services/user.service';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfirmDirective } from '../directives/confirm.directive';
import { ChatService } from '../../chat/chat.service';
import { ThumbnailPipe } from '../pipes/thumbnail.pipe';
import { RoleService } from '../../role/role.service';

@Component({
  selector: 'app-document-owners-list',
  imports: [
    AsyncPipe,
    ConfirmDirective,
    DatePipe,
    NgOptimizedImage,
    MatBadge,
    MatIconButton,
    MatIcon,
    MatTooltip,
    ThumbnailPipe,
  ],
  templateUrl: './document-owners-list.component.html',
  styleUrl: './document-owners-list.component.scss'
})
export class DocumentOwnersListComponent implements OnInit {
  documentId = input.required<number>();

  documentType = input.required<string>();

  documentOwners = signal<DocumentOwner[]>(undefined);

  error = signal<boolean>(false);

  userRoles = signal<Record<number, string>>({});


  private readonly chatService = inject(ChatService);

  private readonly documentOwnersService = inject(DocumentOwnerService);

  private readonly roleService = inject(RoleService);

  private readonly userService = inject(UserService);

  private readonly snackBar = inject(MatSnackBar);

  ngOnInit() {
    this.loadMembers();
    this.loadUserRoles();
  }

  isCurrentUser(member: DocumentOwner): boolean {
    const currentUserId = this.userService.user$.getValue().id;

    return member.owner_type === 'User' && member.owner_id === currentUserId;
  }

  getInitialsFrom(member: DocumentOwner): string {
    return member.owner.name.split(' ').slice(0, 2).map((name) => name.charAt(0).toUpperCase()).join('');
  }

  removeMember(member: DocumentOwner): void {
    this.documentOwnersService.destroy(member.id)
      .pipe(
        catchError(() => {
          this.snackBar.open(
            'Error al remover miembro del documento',
            'Cerrar',
            { duration: 3000, panelClass: ['snackbar-error'] }
          );
          this.error.set(true);
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.snackBar.open(
          'El miembro ha sido removido del documento',
          'Cerrar',
          { duration: 3000, panelClass: ['snackbar-success'] }
        );
        this.loadMembers();
        this.chatService.reloadChats$.next();
      });
  }

  isAdmin() {
    return this.roleService.can('read', 'admin');
  }

  private loadMembers(): void {
    this.documentOwnersService.index(this.documentId(), this.documentType())
      .pipe(
        catchError(() => {
          this.snackBar.open(
            'Error al cargar los propietarios del documento',
            'Cerrar',
            { duration: 3000, panelClass: ['snackbar-error'] }
          );
          this.error.set(true);
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this.documentOwners.set(response.body);
      });
  }

  private loadUserRoles(): void {
    this.userRoles = this.userService.users$.getValue().reduce(function(acc, user) {
        acc[user.id] = user.role ? user.role.name : 'Sin rol';
        return acc;
    }, {});
  }
}
